// ============================================================================
// CANCUN100 — GastosTab.js  (FASE FINANZAS v1 · Parte 2 — subpestaña GASTOS)
// ----------------------------------------------------------------------------
// Usuaria objetivo: Tere (socia administradora, NO técnica). Botones y letras
// grandes, español simple, confirmaciones claras.
//
// ESCRITURA PERMITIDA SOLO en: gastos, gastos_fijos_plantilla.
// LECTURA: finanzas_gastos_mes (comparativo). NO toca ninguna otra tabla/vista
// ni la matemática de comisiones. Storage: bucket 'comprobantes', carpeta gastos/.
// ============================================================================
import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { supabase } from './supabaseClient';
import { RADIO, RADIO_TARJETA } from './tema';
import { ComprobanteFirmado, subirComprobante, camposRuta } from './comprobantes'; // APP 2/07: liga firmada
import Stepper from './Stepper'; // APP 2/05 · F1: día de pago con − n +

const F0 = new Intl.NumberFormat('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const money = (n) => '$' + F0.format(Number(n || 0));
const num = (o, keys) => { for (const k of keys) { if (o && o[k] != null && o[k] !== '') return Number(o[k]) || 0; } return 0; };

const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const hoyYMD = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const curYM = () => hoyYMD().slice(0, 7);
const firstDayOf = (ym) => `${ym}-01`;
const monthLabel = (ym) => { const [y, m] = ym.split('-').map(Number); return `${MESES[m - 1]} ${y}`; };
const prevYM = (ym) => { let [y, m] = ym.split('-').map(Number); m -= 1; if (m < 1) { m = 12; y -= 1; } return `${y}-${String(m).padStart(2, '0')}`; };

const CATS = [
  { key: 'Súper', icon: '🛒' },
  { key: 'Papelería', icon: '📎' },
  { key: 'Mantenimiento', icon: '🔧' },
  { key: 'Gasolina', icon: '⛽' },
  { key: 'Otro', icon: '📦' },
];

// Recalcula los problemas de una fila de la vista previa del import.
const recalc = (concepto, monto) => {
  const probs = [];
  if (!String(concepto || '').trim()) probs.push('Falta el concepto');
  if (monto === '' || monto == null || isNaN(Number(monto))) probs.push('Monto no numérico');
  return probs;
};

export default function GastosTab({ C, adminName = 'Admin' }) {
  const [loading, setLoading] = useState(true);
  const [plantilla, setPlantilla] = useState([]);   // gastos_fijos_plantilla
  const [gastos, setGastos] = useState([]);          // gastos (todos)
  // NOTA: existe la vista finanzas_gastos_mes (agregado por mes), pero el total,
  // el desglose fijos/adicionales y el semáforo se calculan aquí desde `gastos`
  // (misma fuente que alimenta la vista) para mantener el desglose consistente.
  const [ym, setYm] = useState(curYM());
  const [toast, setToast] = useState('');            // mensaje de éxito
  const [busy, setBusy] = useState(false);

  // Bloque 1
  const [editFijo, setEditFijo] = useState(null);    // {plantillaId, valor}
  // Bloque 2 (asistente)
  const [wiz, setWiz] = useState(null);              // {step, categoria, concepto, monto, fecha, file}
  // Bloque 3 (plantilla)
  const [editRow, setEditRow] = useState(null);      // {id, concepto, monto_base, dia_pago}
  const [addRow, setAddRow] = useState(null);        // {concepto, monto, dia}
  const [imp, setImp] = useState(null);              // {rows:[...]}

  const showToast = (m) => { setToast(m); setTimeout(() => setToast(''), 3500); };

  const load = async () => {
    setLoading(true);
    const [p, g] = await Promise.all([
      supabase.from('gastos_fijos_plantilla').select('*'),
      supabase.from('gastos').select('*'),
    ]);
    setPlantilla(p.data || []);
    setGastos(g.data || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  // ── Derivados del mes seleccionado ──
  const firstDay = firstDayOf(ym);
  const mesDe = (g) => String(g.mes || '').slice(0, 7);
  const gastosMes = gastos.filter((g) => mesDe(g) === ym);
  const fijosRegistrados = gastosMes.filter((g) => g.tipo === 'fijo');
  const esporadicos = gastosMes.filter((g) => g.tipo !== 'fijo').sort((a, b) => String(b.fecha_gasto || '').localeCompare(String(a.fecha_gasto || '')));
  const plantillaActiva = plantilla.filter((p) => p.activo !== false).sort((a, b) => num(a, ['orden']) - num(b, ['orden']));
  const regByPlantilla = {}; fijosRegistrados.forEach((g) => { if (g.plantilla_id != null) regByPlantilla[g.plantilla_id] = g; });
  const nRevisados = plantillaActiva.filter((p) => regByPlantilla[p.id]).length;
  const totalCompleto = plantillaActiva.length > 0 && nRevisados === plantillaActiva.length;

  const totalFijosMes = fijosRegistrados.reduce((s, g) => s + num(g, ['monto']), 0);
  const totalEspoMes = esporadicos.reduce((s, g) => s + num(g, ['monto']), 0);
  const totalMes = totalFijosMes + totalEspoMes;
  // Mes anterior (para comparación)
  const ymPrev = prevYM(ym);
  const totalMesPrev = gastos.filter((g) => mesDe(g) === ymPrev).reduce((s, g) => s + num(g, ['monto']), 0);
  const diffPrev = totalMes - totalMesPrev;

  // Semáforo de fijos: registrado difiere >15% de su monto_base
  const alertasFijos = [];
  fijosRegistrados.forEach((g) => {
    const p = plantilla.find((x) => x.id === g.plantilla_id);
    if (!p) return;
    const base = num(p, ['monto_base']);
    if (base <= 0) return;
    const diff = (num(g, ['monto']) - base) / base;
    if (Math.abs(diff) > 0.15) alertasFijos.push({ concepto: p.concepto, pct: Math.round(diff * 100) });
  });

  const hoyDia = new Date().getDate();
  const esMesActual = ym === curYM();

  // ══════════════════════ ESCRITURAS (solo gastos / plantilla) ══════════════
  const registrarFijo = async (p, monto) => {
    setBusy(true);
    const row = { tipo: 'fijo', plantilla_id: p.id, mes: firstDay, concepto: p.concepto, categoria: p.categoria || 'Fijo', monto: Number(monto) || 0, registrado_por: adminName };
    const { error } = await supabase.from('gastos').insert(row);
    setBusy(false);
    if (error) {
      if (String(error.code) === '23505' || /duplicate|unique/i.test(error.message || '')) { showToast('Ese gasto ya estaba registrado este mes.'); }
      else { alert('No se pudo guardar: ' + error.message); return; }
    } else { showToast(`Registrado: ${p.concepto} — ${money(monto)} ✓`); }
    setEditFijo(null);
    await load();
  };
  const corregirFijo = async (g) => {
    if (!window.confirm(`¿Corregir "${g.concepto}"?\n\nSe borra el registro de ${money(g.monto)} para que lo captures de nuevo.`)) return;
    setBusy(true);
    const { error } = await supabase.from('gastos').delete().eq('id', g.id);
    setBusy(false);
    if (error) { alert('No se pudo corregir: ' + error.message); return; }
    await load();
  };

  const guardarEsporadico = async () => {
    const w = wiz;
    if (!w) return;
    const monto = Number(w.monto) || 0;
    if (monto <= 0) { alert('Escribe cuánto fue (mayor a 0).'); return; }
    setBusy(true);
    let comprobante_url = null, comprobante_filename = null;
    try {
      if (w.file) {
        const f = w.file;
        if (f.size > 2 * 1024 * 1024) { alert('La foto no debe pesar más de 2MB.'); setBusy(false); return; }
        if (!['image/jpeg', 'image/png', 'image/jpg'].includes(f.type)) { alert('La foto debe ser JPG o PNG.'); setBusy(false); return; }
        const ext = f.name.split('.').pop();
        const filename = `gastos/gasto_${Date.now()}.${ext}`;
        const sub = await subirComprobante(f, filename); // APP 2/07: ruta + URL (gastos ya guarda la ruta en comprobante_filename)
        comprobante_url = sub.url;
        comprobante_filename = sub.path;
      }
      const concepto = (w.concepto || '').trim() || w.categoria;
      const row = { tipo: 'esporadico', plantilla_id: null, mes: firstDay, concepto, categoria: w.categoria, monto, fecha_gasto: w.fecha || hoyYMD(), comprobante_url, comprobante_filename, ...camposRuta('gastos', comprobante_filename), registrado_por: adminName };
      const { error } = await supabase.from('gastos').insert(row);
      if (error) throw error;
      showToast(`Gasto guardado: ${w.categoria} — ${money(monto)} ✓`);
      setWiz(null);
      await load();
    } catch (e) {
      alert('No se pudo guardar el gasto: ' + (e.message || e));
    } finally { setBusy(false); }
  };
  const borrarEsporadico = async (g) => {
    if (!window.confirm(`¿Eliminar el gasto "${g.concepto}" de ${money(g.monto)}?\n\nEsta acción no se puede deshacer.`)) return;
    setBusy(true);
    try {
      const path = g.comprobante_filename || (g.comprobante_url && String(g.comprobante_url).includes('/comprobantes/') ? String(g.comprobante_url).split('/comprobantes/')[1] : null);
      if (path) await supabase.storage.from('comprobantes').remove([path]);
      const { error } = await supabase.from('gastos').delete().eq('id', g.id);
      if (error) throw error;
      await load();
    } catch (e) { alert('No se pudo eliminar: ' + (e.message || e)); }
    finally { setBusy(false); }
  };

  // Plantilla (bloque 3)
  const guardarEdicion = async () => {
    const e = editRow;
    if (!e.concepto || !e.concepto.trim()) { alert('El concepto no puede estar vacío.'); return; }
    setBusy(true);
    const { error } = await supabase.from('gastos_fijos_plantilla').update({ concepto: e.concepto.trim(), monto_base: Number(e.monto_base) || 0, dia_pago: Number(e.dia_pago) || null }).eq('id', e.id);
    setBusy(false);
    if (error) { alert('No se pudo guardar: ' + error.message); return; }
    setEditRow(null); await load();
  };
  const toggleActivo = async (p) => {
    if (p.activo !== false) {
      if (!window.confirm(`Ya no verás "${p.concepto}" en tu checklist mensual.\n\nSus registros anteriores se conservan. ¿Continuar?`)) return;
    }
    setBusy(true);
    const { error } = await supabase.from('gastos_fijos_plantilla').update({ activo: !(p.activo !== false) }).eq('id', p.id);
    setBusy(false);
    if (error) { alert('No se pudo cambiar: ' + error.message); return; }
    await load();
  };
  const moverOrden = async (p, dir) => {
    const lista = [...plantilla].sort((a, b) => num(a, ['orden']) - num(b, ['orden']));
    const idx = lista.findIndex((x) => x.id === p.id);
    const swap = idx + dir;
    if (swap < 0 || swap >= lista.length) return;
    const a = lista[idx], b = lista[swap];
    setBusy(true);
    await supabase.from('gastos_fijos_plantilla').update({ orden: num(b, ['orden']) }).eq('id', a.id);
    await supabase.from('gastos_fijos_plantilla').update({ orden: num(a, ['orden']) }).eq('id', b.id);
    setBusy(false); await load();
  };
  const agregarFijo = async () => {
    const a = addRow;
    if (!a.concepto || !a.concepto.trim()) { alert('Escribe el nombre del gasto.'); return; }
    const maxOrden = plantilla.reduce((m, x) => Math.max(m, num(x, ['orden'])), 0);
    setBusy(true);
    const { error } = await supabase.from('gastos_fijos_plantilla').insert({ concepto: a.concepto.trim(), monto_base: Number(a.monto) || 0, dia_pago: Number(a.dia) || null, activo: true, orden: maxOrden + 1 });
    setBusy(false);
    if (error) { alert('No se pudo agregar: ' + error.message); return; }
    setAddRow(null); showToast('Gasto fijo agregado ✓'); await load();
  };

  // ── Excel (SheetJS) ──
  const descargarPlantillaXlsx = () => {
    const ws = XLSX.utils.aoa_to_sheet([['Concepto', 'Monto mensual', 'Día de pago'], ['Renta', 25000, 5]]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Gastos fijos');
    XLSX.writeFile(wb, 'plantilla_gastos_fijos.xlsx');
  };
  const leerExcel = async (file) => {
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, blankrows: false });
      const body = aoa.slice(1); // salta encabezado
      const rows = body.map((r) => {
        const concepto = String(r[0] == null ? '' : r[0]).trim();
        const montoRaw = r[1];
        const dia = r[2] == null || r[2] === '' ? '' : Number(r[2]);
        const montoNum = Number(String(montoRaw).replace(/[$,\s]/g, ''));
        const problemas = [];
        if (!concepto) problemas.push('Falta el concepto');
        if (montoRaw == null || montoRaw === '' || isNaN(montoNum)) problemas.push('Monto no numérico');
        const dup = plantilla.find((p) => (p.concepto || '').trim().toLowerCase() === concepto.toLowerCase() && concepto);
        return { concepto, monto: isNaN(montoNum) ? '' : montoNum, dia, problemas, dupId: dup ? dup.id : null, action: dup ? '' : 'insert' };
      }).filter((r) => r.concepto || r.monto !== '');
      if (rows.length === 0) { alert('El archivo no tiene filas para cargar.'); return; }
      setImp({ rows });
    } catch (e) { alert('No se pudo leer el Excel: ' + (e.message || e)); }
  };
  const cargarImport = async () => {
    const validas = imp.rows.filter((r) => r.concepto && r.monto !== '' && !isNaN(Number(r.monto)));
    const pendientesDup = validas.filter((r) => r.dupId && !r.action);
    if (pendientesDup.length > 0) { alert(`Elige "Actualizar monto" o "Ignorar" en las ${pendientesDup.length} fila(s) repetida(s) antes de cargar.`); return; }
    setBusy(true);
    let n = 0, maxOrden = plantilla.reduce((m, x) => Math.max(m, num(x, ['orden'])), 0);
    try {
      for (const r of validas) {
        if (r.dupId) {
          if (r.action === 'actualizar') {
            const patch = { monto_base: Number(r.monto) }; if (r.dia !== '' && !isNaN(Number(r.dia))) patch.dia_pago = Number(r.dia);
            const { error } = await supabase.from('gastos_fijos_plantilla').update(patch).eq('id', r.dupId);
            if (!error) n++;
          } // 'ignorar' → nada
        } else {
          maxOrden += 1;
          const { error } = await supabase.from('gastos_fijos_plantilla').insert({ concepto: r.concepto.trim(), monto_base: Number(r.monto), dia_pago: r.dia !== '' && !isNaN(Number(r.dia)) ? Number(r.dia) : null, activo: true, orden: maxOrden });
          if (!error) n++;
        }
      }
      setImp(null); showToast(`Listo: ${n} gasto(s) fijo(s) cargado(s) ✓`); await load();
    } catch (e) { alert('Error al cargar: ' + (e.message || e)); }
    finally { setBusy(false); }
  };

  // ══════════════════════ ESTILOS — sistema Aurelia (clases .au-*) ══════════
  // 100% visual: se sustituyen los estilos ad-hoc por las clases del Arki
  // (au-glass, au-btn, au-input, au-badge, au-banner, au-switch) y tokens --au-*.
  const glassPad = { padding: 20, marginBottom: 16 };                       // relleno de tarjeta au-glass
  const label = { fontSize: 12, color: 'var(--au-text-faint)', fontWeight: 500, letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 6 };
  const Hd = ({ children }) => <h2 style={{ fontSize: 22, fontWeight: 600, color: 'var(--au-text-primary)', margin: 0, fontFamily: 'var(--au-font-text)' }}>{children}</h2>;
  const brand = { background: `${C.grad}`, color: '#fff', boxShadow:'none' }; // botón de marca (casa) a escala au-btn
  const okBtn = { borderColor: 'var(--au-success-bd)', color: 'var(--au-success-fg)' };  // acento verde (confirmar) sobre au-btn--secondary
  const disp = { fontFamily: 'var(--au-font-text)' };                    // números destacados (una sola fuente, Bloque 25; antes Sora)
  const smallBtn = { padding: '6px 12px', fontSize: 12 };                   // au-btn compacto para tablas

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 90 }}>
        <div style={{ width: 40, height: 40, border: `3px solid var(--au-glass-border)`, borderTop: `3px solid var(--au-gold-solid)`, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        <span style={{ marginLeft: 14, color: 'var(--au-text-faint)', fontSize: 14 }}>Cargando gastos…</span>
      </div>
    );
  }

  const mesInput = (
    <input type="month" className="au-input au-num" value={ym} onChange={(e) => setYm(e.target.value || curYM())}
      style={{ width: 'auto', cursor: 'pointer', colorScheme: 'dark' }} />
  );

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      {/* Toast */}
      {toast && (
        <div className="au-banner au-banner--success" style={{ position: 'fixed', top: 80, left: '50%', transform: 'translateX(-50%)', zIndex: 400, fontWeight: 600, boxShadow:'var(--au-shadow-card)' }}>{toast}</div>
      )}

      {/* ══ Encabezado — Resumen del mes ══ */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <Hd>Gastos de {monthLabel(ym)}</Hd>
        {mesInput}
      </div>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 12 }}>
        <div className="au-glass" style={{ flex: '1 1 260px', padding: '18px 22px', borderColor: 'var(--au-gold-border)' }}>
          <div style={label}>Total del mes</div>
          <div className="au-num" style={{ fontSize: 30, fontWeight: 600, color: 'var(--au-text-primary)', ...disp, lineHeight: 1.1 }}>{money(totalMes)}</div>
          <div style={{ fontSize: 12, color: 'var(--au-text-faint)', marginTop: 4 }}>Fijos {money(totalFijosMes)} + adicionales {money(totalEspoMes)}</div>
        </div>
        <div className="au-glass" style={{ flex: '1 1 220px', padding: '18px 22px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <div style={label}>Comparado con {monthLabel(ymPrev)}</div>
          {totalMesPrev === 0 ? (
            <div style={{ fontSize: 14, color: 'var(--au-text-faint)', fontWeight: 500, marginTop: 6 }}>Sin datos del mes anterior</div>
          ) : (
            <div className="au-num" style={{ fontSize: 22, fontWeight: 600, marginTop: 6, ...disp, color: diffPrev > 0 ? 'var(--au-error-fg)' : 'var(--au-success-fg)' }}>
              {diffPrev > 0 ? '▲' : '▼'} {money(Math.abs(diffPrev))} <span style={{ fontSize: 14, color: 'var(--au-text-faint)', fontWeight: 400, fontFamily: 'var(--au-font-text)' }}>{diffPrev > 0 ? 'más' : 'menos'} que {MESES[(Number(ymPrev.split('-')[1]) - 1)]}</span>
            </div>
          )}
        </div>
      </div>
      {alertasFijos.length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
          {alertasFijos.map((a, i) => (
            <span key={i} className="au-badge au-badge--yellow">
              ⚠ {a.concepto} {a.pct > 0 ? 'subió' : 'bajó'} {Math.abs(a.pct)}% este mes
            </span>
          ))}
        </div>
      )}

      {/* ══ BLOQUE 1 — Revisión del mes (checklist de fijos) ══ */}
      <div className="au-glass" style={glassPad}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <Hd>Gastos fijos de {monthLabel(ym)}</Hd>
          <span className={`au-badge ${totalCompleto ? 'au-badge--success' : 'au-badge--blue'}`}>{nRevisados} de {plantillaActiva.length} revisados</span>
        </div>

        {esMesActual && hoyDia >= 25 && !totalCompleto && (
          <div className="au-banner" style={{ color: 'var(--au-yellow-fg)', background: 'var(--au-yellow-bg)', borderColor: 'var(--au-yellow-bd)', margin: '12px 0' }}>
            📅 Ya casi es cierre de mes (día 30) — revisa tus gastos fijos.
          </div>
        )}
        {totalCompleto && (
          <div className="au-banner au-banner--success" style={{ margin: '12px 0' }}>
            🎉 Mes revisado completo — total de fijos {money(totalFijosMes)}
          </div>
        )}

        <div style={{ marginTop: 8 }}>
          {plantillaActiva.length === 0 && <div style={{ color: 'var(--au-text-faint)', fontSize: 14, padding: '10px 0' }}>Aún no tienes gastos fijos. Agrégalos abajo en “Mis gastos fijos”.</div>}
          {plantillaActiva.map((p) => {
            const reg = regByPlantilla[p.id];
            const editing = editFijo && editFijo.plantillaId === p.id;
            if (reg) {
              return (
                <div key={p.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '12px 14px', borderRadius: RADIO_TARJETA, background: 'var(--au-glass-fill)', border: '1px solid var(--au-glass-border)', marginBottom: 8, flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--au-text-secondary)' }}>✓ {p.concepto} — <span className="au-num" style={{ color: 'var(--au-text-primary)', fontWeight: 600 }}>{money(reg.monto)}</span></div>
                  <button onClick={() => corregirFijo(reg)} disabled={busy} className="au-btn au-btn--ghost" style={smallBtn}>corregir</button>
                </div>
              );
            }
            return (
              <div key={p.id} style={{ padding: 16, borderRadius: RADIO_TARJETA, background: 'var(--au-glass-fill)', border: '1px solid var(--au-glass-border)', marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--au-text-primary)' }}>{p.concepto}</div>
                    <div className="au-num" style={{ fontSize: 30, fontWeight: 600, color: 'var(--au-text-primary)', ...disp }}>{money(p.monto_base)}</div>
                  </div>
                  {!editing && (
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      <button onClick={() => registrarFijo(p, num(p, ['monto_base']))} disabled={busy} className="au-btn au-btn--secondary" style={okBtn}>✓ Igual que siempre</button>
                      <button onClick={() => setEditFijo({ plantillaId: p.id, valor: String(num(p, ['monto_base'])) })} disabled={busy} className="au-btn au-btn--secondary">✏ Cambió el monto</button>
                    </div>
                  )}
                </div>
                {editing && (
                  <div style={{ marginTop: 14 }}>
                    <div style={label}>¿Cuánto fue este mes?</div>
                    <input type="number" inputMode="decimal" className="au-input au-num" value={editFijo.valor} autoFocus
                      onChange={(e) => setEditFijo({ ...editFijo, valor: e.target.value })} style={{ fontSize: 22, ...disp }} />
                    <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
                      <button onClick={() => registrarFijo(p, Number(editFijo.valor) || 0)} disabled={busy} className="au-btn au-btn--secondary" style={okBtn}>Guardar {money(Number(editFijo.valor) || 0)}</button>
                      <button onClick={() => setEditFijo(null)} disabled={busy} className="au-btn au-btn--ghost">Cancelar</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ══ BLOQUE 2 — Registrar gasto adicional ══ */}
      <div className="au-glass" style={glassPad}>
        <Hd>Gasto adicional</Hd>
        <div style={{ fontSize: 14, color: 'var(--au-text-faint)', margin: '4px 0 14px' }}>Compras del día a día que no son fijas.</div>
        <button onClick={() => setWiz({ step: 1, categoria: '', concepto: '', monto: '', fecha: hoyYMD(), file: null })} className="au-btn" style={{ ...brand, width: '100%', justifyContent: 'center', padding: '14px 22px', fontSize: 14 }}>
          ➕ Registrar gasto
        </button>

        {/* Lista de esporádicos del mes */}
        <div style={{ marginTop: 20 }}>
          <div style={{ ...label, marginBottom: 10 }}>Gastos adicionales de {monthLabel(ym)} ({esporadicos.length})</div>
          {esporadicos.length === 0 && <div style={{ color: 'var(--au-text-faint)', fontSize: 14 }}>Todavía no hay gastos adicionales este mes.</div>}
          {esporadicos.map((g) => (
            <div key={g.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderRadius: RADIO_TARJETA, background: 'var(--au-glass-fill)', border: '1px solid var(--au-glass-border)', marginBottom: 8, flexWrap: 'wrap' }}>
              <div className="au-num" style={{ fontSize: 12, color: 'var(--au-text-faint)', minWidth: 92 }}>{g.fecha_gasto || '—'}</div>
              <div style={{ flex: 1, minWidth: 120 }}>
                <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--au-text-primary)' }}>{g.concepto}</div>
                {g.categoria && g.categoria !== g.concepto && <div style={{ fontSize: 12, color: 'var(--au-text-faint)' }}>{g.categoria}</div>}
              </div>
              <div className="au-num" style={{ fontSize: 16, fontWeight: 600, color: 'var(--au-text-primary)', ...disp }}>{money(g.monto)}</div>
              {g.comprobante_url && <ComprobanteFirmado url={g.comprobante_url} path={g.comprobante_path || g.comprobante_filename}>{(href) => <a href={href} target="_blank" rel="noreferrer" className="au-badge au-badge--blue" style={{ textDecoration: 'none' }}>🧾 ticket</a>}</ComprobanteFirmado>}
              <button onClick={() => borrarEsporadico(g)} disabled={busy} className="au-btn au-btn--ghost" style={{ ...smallBtn, color: 'var(--au-error-fg)' }}>🗑</button>
            </div>
          ))}
        </div>
      </div>

      {/* ══ BLOQUE 3 — Mis gastos fijos (plantilla) ══ */}
      <div className="au-glass" style={glassPad}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 6 }}>
          <Hd>Mis gastos fijos</Hd>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={() => setAddRow({ concepto: '', monto: '', dia: '' })} className="au-btn au-btn--secondary" style={{ ...smallBtn, color: 'var(--au-blue-fg)', borderColor: 'var(--au-blue-bd)' }}>➕ Agregar gasto fijo</button>
            <button onClick={() => document.getElementById('imp-file').click()} className="au-btn au-btn--secondary" style={smallBtn}>📥 Importar desde Excel</button>
            <input id="imp-file" type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={(e) => { const f = e.target.files[0]; e.target.value = ''; if (f) leerExcel(f); }} />
          </div>
        </div>
        <div style={{ fontSize: 12, color: 'var(--au-text-faint)', marginBottom: 12 }}>
          <button onClick={descargarPlantillaXlsx} className="au-btn au-btn--ghost" style={{ padding: 0, fontSize: 12, color: 'var(--au-blue-fg)', textDecoration: 'underline' }}>Descargar plantilla de Excel</button> (Concepto · Monto mensual · Día de pago)
        </div>

        {addRow && (
          <div style={{ background: 'var(--au-glass-fill)', border: '1px dashed var(--au-blue-bd)', borderRadius: RADIO_TARJETA, padding: 14, marginBottom: 14, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ flex: '2 1 160px' }}><div style={label}>Concepto</div><input className="au-input" value={addRow.concepto} onChange={(e) => setAddRow({ ...addRow, concepto: e.target.value })} placeholder="Ej: Renta" /></div>
            <div style={{ flex: '1 1 110px' }}><div style={label}>Monto</div><input type="number" className="au-input au-num" value={addRow.monto} onChange={(e) => setAddRow({ ...addRow, monto: e.target.value })} placeholder="0" /></div>
            <div style={{ flex: '1 1 130px' }}><div style={label}>Día de pago</div><Stepper chico value={addRow.dia || 1} min={1} max={31} label="día de pago" onChange={(n) => setAddRow({ ...addRow, dia: String(n) })} /></div>
            <button onClick={agregarFijo} disabled={busy} className="au-btn au-btn--secondary" style={okBtn}>Guardar</button>
            <button onClick={() => setAddRow(null)} className="au-btn au-btn--ghost">Cancelar</button>
          </div>
        )}

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
            <thead><tr>
              {['', 'Concepto', 'Monto', 'Día', 'Activo', ''].map((h, i) => <th key={i} style={{ textAlign: i > 1 && i < 5 ? 'right' : 'left', fontSize: 12, color: 'var(--au-text-faint)', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 500, padding: '8px 10px', borderBottom: '1px solid var(--au-glass-border)' }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {plantilla.sort((a, b) => num(a, ['orden']) - num(b, ['orden'])).map((p, idx, arr) => {
                const ed = editRow && editRow.id === p.id;
                const inactivo = p.activo === false;
                return (
                  <tr key={p.id} style={{ opacity: inactivo ? 0.5 : 1 }}>
                    <td style={{ padding: '8px 6px', borderBottom: '1px solid var(--au-glass-border)', whiteSpace: 'nowrap' }}>
                      <button onClick={() => moverOrden(p, -1)} disabled={idx === 0 || busy} style={{ background: 'none', border: 'none', color: 'var(--au-text-faint)', cursor: 'pointer', fontSize: 16 }}>↑</button>
                      <button onClick={() => moverOrden(p, 1)} disabled={idx === arr.length - 1 || busy} style={{ background: 'none', border: 'none', color: 'var(--au-text-faint)', cursor: 'pointer', fontSize: 16 }}>↓</button>
                    </td>
                    <td style={{ padding: '8px 10px', borderBottom: '1px solid var(--au-glass-border)', color: 'var(--au-text-primary)', fontSize: 14, fontWeight: 500 }}>
                      {ed ? <input className="au-input" value={editRow.concepto} onChange={(e) => setEditRow({ ...editRow, concepto: e.target.value })} style={{ padding: '8px 10px' }} /> : p.concepto}
                    </td>
                    <td className="au-num" style={{ padding: '8px 10px', borderBottom: '1px solid var(--au-glass-border)', textAlign: 'right', color: 'var(--au-text-primary)', ...disp, fontWeight: 600 }}>
                      {ed ? <input type="number" className="au-input au-num" value={editRow.monto_base} onChange={(e) => setEditRow({ ...editRow, monto_base: e.target.value })} style={{ padding: '8px 10px', textAlign: 'right' }} /> : money(p.monto_base)}
                    </td>
                    <td className="au-num" style={{ padding: '8px 10px', borderBottom: '1px solid var(--au-glass-border)', textAlign: 'right', color: 'var(--au-text-faint)' }}>
                      {ed ? <Stepper chico ancho={130} value={editRow.dia_pago || 1} min={1} max={31} label="día de pago" onChange={(n) => setEditRow({ ...editRow, dia_pago: String(n) })} /> : (p.dia_pago || '—')}
                    </td>
                    <td style={{ padding: '8px 10px', borderBottom: '1px solid var(--au-glass-border)', textAlign: 'right' }}>
                      <button onClick={() => toggleActivo(p)} disabled={busy} title={inactivo ? 'Activar' : 'Desactivar'} className="au-switch" data-on={!inactivo} style={{ display: 'inline-block' }}>
                        <span className="au-switch__knob" />
                      </button>
                    </td>
                    <td style={{ padding: '8px 10px', borderBottom: '1px solid var(--au-glass-border)', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {ed ? (
                        <>
                          <button onClick={guardarEdicion} disabled={busy} className="au-btn au-btn--secondary" style={{ ...smallBtn, ...okBtn, marginRight: 6 }}>Guardar</button>
                          <button onClick={() => setEditRow(null)} className="au-btn au-btn--ghost" style={smallBtn}>✕</button>
                        </>
                      ) : (
                        <button onClick={() => setEditRow({ id: p.id, concepto: p.concepto || '', monto_base: String(num(p, ['monto_base'])), dia_pago: p.dia_pago == null ? '' : String(p.dia_pago) })} className="au-btn au-btn--ghost" style={{ ...smallBtn, color: 'var(--au-blue-fg)' }}>editar</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ══ ASISTENTE gasto adicional (3 pasos) ══ */}
      {wiz && (
        <div onClick={() => setWiz(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 350, padding: 16, backdropFilter: 'blur(6px)' }}>
          <div onClick={(e) => e.stopPropagation()} className="au-glass au-glass--strong" style={{ maxWidth: 480, width: '100%', padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ ...label, marginBottom: 0 }}>Paso {wiz.step} de 3</div>
              <button onClick={() => setWiz(null)} className="au-btn au-btn--ghost" style={{ padding: 0, width: 32, height: 32, justifyContent: 'center' }}>✕</button>
            </div>

            {wiz.step === 1 && (
              <>
                <Hd>¿Qué compraste?</Hd>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: 10, margin: '16px 0' }}>
                  {CATS.map((c) => (
                    <button key={c.key} onClick={() => setWiz({ ...wiz, categoria: c.key })} className="au-btn au-btn--secondary"
                      style={{ flexDirection: 'column', padding: '18px 10px', height: 'auto', ...(wiz.categoria === c.key ? { borderColor: 'var(--au-gold-border)', background: 'var(--au-gold-bg)', color: 'var(--au-gold-solid)' } : {}) }}>
                      <div style={{ fontSize: 30, marginBottom: 4 }}>{c.icon}</div>{c.key}
                    </button>
                  ))}
                </div>
                <div style={label}>Detalle (opcional){wiz.categoria === 'Otro' ? ' — escribe qué fue' : ''}</div>
                <input className="au-input" value={wiz.concepto} onChange={(e) => setWiz({ ...wiz, concepto: e.target.value })} placeholder="Ej: Walmart, Office Depot…" />
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
                  <button disabled={!wiz.categoria || (wiz.categoria === 'Otro' && !wiz.concepto.trim())} onClick={() => setWiz({ ...wiz, step: 2 })}
                    className="au-btn" style={{ ...brand, opacity: (wiz.categoria && !(wiz.categoria === 'Otro' && !wiz.concepto.trim())) ? 1 : 0.5 }}>Siguiente →</button>
                </div>
              </>
            )}

            {wiz.step === 2 && (
              <>
                <Hd>¿Cuánto fue?</Hd>
                <div style={{ ...label, marginTop: 16 }}>Monto</div>
                <input type="number" inputMode="decimal" autoFocus className="au-input au-num" value={wiz.monto} onChange={(e) => setWiz({ ...wiz, monto: e.target.value })} placeholder="0" style={{ fontSize: 30, ...disp }} />
                <div style={{ ...label, marginTop: 16 }}>Fecha</div>
                <input type="date" className="au-input au-num" value={wiz.fecha} onChange={(e) => setWiz({ ...wiz, fecha: e.target.value })} style={{ colorScheme: 'dark' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
                  <button onClick={() => setWiz({ ...wiz, step: 1 })} className="au-btn au-btn--ghost">← Atrás</button>
                  <button disabled={!(Number(wiz.monto) > 0)} onClick={() => setWiz({ ...wiz, step: 3 })} className="au-btn" style={{ ...brand, opacity: Number(wiz.monto) > 0 ? 1 : 0.5 }}>Siguiente →</button>
                </div>
              </>
            )}

            {wiz.step === 3 && (
              <>
                <Hd>Foto del ticket</Hd>
                <div style={{ fontSize: 14, color: 'var(--au-text-faint)', margin: '4px 0 16px' }}>Opcional. JPG o PNG, máximo 2MB.</div>
                <button onClick={() => document.getElementById('wiz-file').click()} className="au-btn au-btn--secondary" style={{ width: '100%', padding: 22, borderStyle: 'dashed' }}>
                  📷 {wiz.file ? wiz.file.name.slice(0, 28) : 'Tomar / elegir foto'}
                </button>
                <input id="wiz-file" type="file" accept="image/jpeg,image/png,image/jpg" style={{ display: 'none' }} onChange={(e) => setWiz({ ...wiz, file: e.target.files[0] || null })} />
                <div style={{ background: 'var(--au-glass-fill)', border: '1px solid var(--au-glass-border)', borderRadius: RADIO_TARJETA, padding: 14, margin: '16px 0', fontSize: 14, color: 'var(--au-text-primary)' }}>
                  <strong>{wiz.categoria}</strong>{wiz.concepto ? ` · ${wiz.concepto}` : ''} — <strong className="au-num" style={disp}>{money(Number(wiz.monto) || 0)}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
                  <button onClick={() => setWiz({ ...wiz, step: 2 })} disabled={busy} className="au-btn au-btn--ghost">← Atrás</button>
                  <button onClick={guardarEsporadico} disabled={busy} className="au-btn au-btn--secondary" style={okBtn}>{busy ? 'Guardando…' : '💾 Guardar gasto'}</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ══ IMPORTAR EXCEL — vista previa ══ */}
      {imp && (
        <div onClick={() => setImp(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 350, padding: 16, backdropFilter: 'blur(6px)' }}>
          <div onClick={(e) => e.stopPropagation()} className="au-glass au-glass--strong" style={{ maxWidth: 760, width: '100%', maxHeight: '88vh', overflow: 'auto', padding: 22 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <Hd>Vista previa de la importación</Hd>
              <button onClick={() => setImp(null)} className="au-btn au-btn--ghost" style={{ padding: 0, width: 32, height: 32, justifyContent: 'center' }}>✕</button>
            </div>
            <div style={{ fontSize: 14, color: 'var(--au-text-faint)', margin: '6px 0 14px' }}>Revisa antes de cargar. Las filas en amarillo tienen un problema. Los repetidos requieren que elijas qué hacer.</div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 620 }}>
                <thead><tr>{['Concepto', 'Monto', 'Día', 'Estado'].map((h, i) => <th key={i} style={{ textAlign: i === 1 || i === 2 ? 'right' : 'left', fontSize: 12, color: 'var(--au-text-faint)', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 500, padding: '8px 10px', borderBottom: '1px solid var(--au-glass-border)' }}>{h}</th>)}</tr></thead>
                <tbody>
                  {imp.rows.map((r, i) => {
                    const setR = (patch) => setImp({ rows: imp.rows.map((x, j) => j === i ? { ...x, ...patch } : x) });
                    const problema = r.problemas.length > 0;
                    const bg = problema ? 'var(--au-yellow-bg)' : 'transparent';
                    return (
                      <tr key={i} style={{ background: bg }}>
                        <td style={{ padding: '6px 10px', borderBottom: '1px solid var(--au-glass-border)' }}>
                          <input className="au-input" value={r.concepto} onChange={(e) => setR({ concepto: e.target.value, problemas: recalc(e.target.value, r.monto) })} style={{ padding: '6px 8px', fontSize: 14 }} />
                        </td>
                        <td style={{ padding: '6px 10px', borderBottom: '1px solid var(--au-glass-border)', textAlign: 'right' }}>
                          <input type="number" className="au-input au-num" value={r.monto} onChange={(e) => setR({ monto: e.target.value === '' ? '' : Number(e.target.value), problemas: recalc(r.concepto, e.target.value) })} style={{ padding: '6px 8px', fontSize: 14, width: 100, textAlign: 'right' }} />
                        </td>
                        <td style={{ padding: '6px 10px', borderBottom: '1px solid var(--au-glass-border)', textAlign: 'right' }}>
                          <Stepper chico ancho={130} value={r.dia || 1} min={1} max={31} label="día de pago" onChange={(n) => setR({ dia: n })} />
                        </td>
                        <td style={{ padding: '6px 10px', borderBottom: '1px solid var(--au-glass-border)', fontSize: 12 }}>
                          {problema ? <span className="au-badge au-badge--yellow">{r.problemas.join(', ')}</span>
                            : r.dupId ? (
                              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                                <span style={{ color: 'var(--au-yellow-fg)' }}>Ya existe:</span>
                                <button onClick={() => setR({ action: 'actualizar' })} className={`au-btn ${r.action === 'actualizar' ? 'au-btn--secondary' : 'au-btn--ghost'}`} style={{ ...smallBtn, padding: '4px 8px', fontSize: 12, ...okBtn }}>Actualizar monto</button>
                                <button onClick={() => setR({ action: 'ignorar' })} className={`au-btn ${r.action === 'ignorar' ? 'au-btn--secondary' : 'au-btn--ghost'}`} style={{ ...smallBtn, padding: '4px 8px', fontSize: 12 }}>Ignorar</button>
                              </div>
                            ) : <span className="au-badge au-badge--success">✓ Nuevo</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
              <button onClick={() => setImp(null)} className="au-btn au-btn--ghost">Cancelar</button>
              <button onClick={cargarImport} disabled={busy} className="au-btn au-btn--secondary" style={okBtn}>
                Cargar {imp.rows.filter((r) => r.concepto && r.monto !== '' && !isNaN(Number(r.monto)) && !(r.dupId && r.action === 'ignorar')).length} gastos fijos
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
